/* eslint-disable no-plusplus */
/* eslint-disable consistent-return */
import React, { useEffect, useRef } from 'react';

interface LineAnimationArtProps {
  width?: number;
  height?: number;
  speed?: number;
}

export function LineAnimationArt({
  width = 1280,
  height = 720,
  speed = 0.02,
}: LineAnimationArtProps): React.ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const centerX = width / 2;
    const centerY = height / 2;

    const draw = () => {
      // Clear canvas with white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // Draw complex line animations
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1;

      const time = timeRef.current * speed;

      // Animated concentric circles with rotation
      for (let i = 1; i <= 5; i++) {
        const radius = (i * width) / 15;
        const rotation = time * (0.1 / i);

        ctx.beginPath();
        for (let angle = 0; angle < Math.PI * 2; angle += 0.05) {
          const x = centerX + Math.cos(angle + rotation) * radius;
          const y = centerY + Math.sin(angle + rotation) * radius;

          if (angle === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.closePath();
        ctx.stroke();
      }

      // Animated radial lines with wave distortion
      ctx.lineWidth = 1.5;
      const numLines = 12;
      for (let i = 0; i < numLines; i++) {
        const angle = (i / numLines) * Math.PI * 2;
        const maxDistance = Math.min(width, height) / 2.5;

        ctx.beginPath();
        for (let dist = 0; dist <= maxDistance; dist += 5) {
          const waveOffset = Math.sin(dist * 0.02 + time * 0.1 + i) * 15;
          const x = centerX + Math.cos(angle) * (dist + waveOffset);
          const y = centerY + Math.sin(angle) * (dist + waveOffset);

          if (dist === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
      }

      // Animated sine wave patterns
      ctx.lineWidth = 1;
      const waveAmplitude = 30;
      const waveFrequency = 0.05;

      for (let offset = -2; offset <= 2; offset++) {
        ctx.beginPath();
        const yStart = centerY + offset * 40;

        for (let x = 0; x < width; x += 2) {
          const y =
            yStart + Math.sin(x * waveFrequency + time * 0.15) * waveAmplitude;

          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.stroke();
      }

      // Animated grid with perspective distortion
      ctx.lineWidth = 0.8;
      ctx.strokeStyle = '#cccccc';

      const gridSize = 60;
      const perspective = Math.sin(time * 0.05) * 0.3 + 0.8;

      for (let x = 0; x < width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x + Math.sin(time * 0.1) * 30, height);
        ctx.stroke();
      }

      for (let y = 0; y < height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y + Math.cos(time * 0.08) * 20);
        ctx.stroke();
      }

      timeRef.current += 1;
      animationRef.current = requestAnimationFrame(draw);
    };

    animationRef.current = requestAnimationFrame(draw);

    return (): void => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [width, height, speed]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ display: 'block' }}
    />
  );
}
